import { useCallback, useMemo } from 'react';
import { unifySubscriptionItems } from '~/src/services/api/api-content/cities/format';
import { TAdditionalProduct } from '~/src/services/api/api-content/cities/types';
import { IFormattedTariff } from '~/src/services/tariffs/types';
import { IOfferContent } from '@r1-frontend/shared/types/tariff/IOfferContent';

interface IProps<T = IFormattedTariff[]> {
    bundles: T;
    internet: T;
    getFilteredTariffs: (tariffs: T, aliases: string) => T;
    bundlesAliases: string;
    internetAliases: string;
}

interface IUsePrepareTariff<T> {
    bundles: T;
    mono: T;
}

export const usePrepareTariff = <T extends IFormattedTariff[]>(
    { bundles, internet, getFilteredTariffs, bundlesAliases, internetAliases }: IProps<T>
): IUsePrepareTariff<T> => {

    // Для ТП "Гига" имеется структура salePackageLinks в тарифе, в которой лежат комбинации подписок,
    // для данного типа ТП необходимо выводить все подписки (уникальные), а также приставку мувикс из обязательной в оборудованиии
    // для остальных тарифов без изменений
    const prepareSubscriptions = useCallback((tariffs) => {
        return tariffs.length
            ? tariffs.map((tariff: IFormattedTariff) => {
                const subscriptionsList = tariff.salePackageLinks;
                if (subscriptionsList && subscriptionsList.length !== 0 && tariff.alias?.includes('giga')) {
                    const unifiedSubscriptions = unifySubscriptionItems(subscriptionsList);
                    const convertedSubscriptions = convertSubscriptionsBody(unifiedSubscriptions, tariff.additionalProducts);

                    return { ...tariff, offerContents: convertedSubscriptions };
                }
                return tariff;
            })
            : [];
    }, [unifySubscriptionItems]);

    const prepareTariff = useCallback((tariffs, aliases) => {
        const tariffsRa = tariffs ? tariffs.filter(item => item.hasRequiredEquipment === false && item.isCottage === false) : [];
        const filteredTariffs = (tariffs && aliases) ? getFilteredTariffs(tariffs, aliases) : [];
        const resultiveTariffs = filteredTariffs.length ? filteredTariffs : tariffsRa;

        return prepareSubscriptions(resultiveTariffs);
    }, [getFilteredTariffs]);

    const bundlesArray = useMemo(
        () => prepareTariff(bundles, bundlesAliases),
        [prepareTariff, bundles, bundlesAliases]
    );

    const internetArray = useMemo(
        () => prepareTariff(internet, internetAliases),
        [prepareTariff, internet, internetAliases]
    );

    return {
        bundles: bundlesArray as T,
        mono: internetArray as T,
    };
};

const convertSubscriptionsBody = (offerContents: IOfferContent[], additionalProducts: TAdditionalProduct[]) => {
    const _offerContents = offerContents.map((item: IOfferContent) => ({
        id: item.id,
        image: item.image,
        imageWebp: item.image_webp,
        width: item.image_width,
        alt: item.title,
        type: 'subscription',
    }));

    const _additionalProducts = additionalProducts
        ?.filter(({ product_type }) => product_type === 'tv')
        .map(({ id, shopItem }) => ({
            id: id,
            image: shopItem?.image_for_tariff_card || '',
            imageWebp: shopItem?.image_for_tariff_card_webp || '',
            width: shopItem?.image_width,
            alt: shopItem?.title || '',
            type: 'equipment',
        }));

    return [..._offerContents, ...(_additionalProducts ?? [])];
};
